import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Calendar, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get proper image URL for display
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If it's a relative path, prepend the backend URL
  return `${BACKEND_URL}${imagePath}`;
};

// Rich text editor toolbar configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'strike',
  'list',
  'link'
];

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    date: new Date().getFullYear().toString(),
    content: '',
    short_content: '',
    image_url: '',
    images: []
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news?sort_by=created_at`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('admin_token');
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        const response = await axios.post(`${API}/admin/upload`, uploadFormData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedUrls.push(response.data.url);  // Store relative URL for portability
      }

      setFormData(prev => ({
        ...prev,
        images: [...(prev.images || []), ...uploadedUrls],
        image_url: prev.image_url || uploadedUrls[0] // Set first image as primary if none exists
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Session expired. Please log in again.');
        localStorage.removeItem('admin_token');
        window.location.href = '/admin';
      } else {
        alert('Error uploading images. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      if (editingArticle) {
        await axios.put(`${API}/admin/news/${editingArticle.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/news`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      fetchNews();
      closeModal();
    } catch (error) {
      console.error('Error saving article:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        alert('Session expired. Please log in again.');
        localStorage.removeItem('admin_token');
        window.location.href = '/admin';
      } else {
        alert('Error saving article. Please try again.');
      }
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/admin/news/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNews();
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Error deleting article');
    }
  };

  const openModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setFormData({
        title: article.title || '',
        slug: article.slug || '',
        date: article.date || new Date().getFullYear().toString(),
        content: article.content || '',
        short_content: article.short_content || '',
        image_url: article.image_url || '',
        images: article.images || []
      });
    } else {
      setEditingArticle(null);
      setFormData({
        title: '',
        slug: '',
        date: new Date().getFullYear().toString(),
        content: '',
        short_content: '',
        image_url: '',
        images: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingArticle(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin h-12 w-12 border-4 border-[#20B2AA] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-black mb-2">News Management</h1>
          <p className="text-gray-600">Manage news articles and announcements</p>
        </div>
        <Button
          onClick={() => openModal()}
          className="bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-xl px-6 py-6 shadow-lg"
        >
          <Plus className="mr-2 h-5 w-5" />
          Add Article
        </Button>
      </div>

      {/* News List */}
      <div className="space-y-4">
        {news.map((article) => (
          <div key={article.id} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
            <div className="flex gap-6">
              {article.image_url && (
                <img
                  src={article.image_url}
                  alt={article.title}
                  className="w-48 h-32 object-cover rounded-xl flex-shrink-0"
                />
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-xl text-black line-clamp-2">{article.title}</h3>
                  <div className="flex gap-2 ml-4 flex-shrink-0">
                    <Button
                      onClick={() => openModal(article)}
                      className="bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-lg"
                      size="sm"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(article.id)}
                      className="bg-red-500 hover:bg-red-600 text-white rounded-lg"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <Calendar className="h-4 w-4" />
                  <span>{article.date}</span>
                </div>
                
                <p className="text-gray-600 line-clamp-2">{article.short_content || article.content.substring(0, 150) + '...'}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-3xl">
              <h2 className="text-2xl font-bold text-black">
                {editingArticle ? 'Edit Article' : 'Add New Article'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  required
                />
              </div>

              {/* SEO Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  SEO URL Slug
                </label>
                <div className="flex items-center rounded-xl border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-[#20B2AA]">
                  <span className="px-3 py-3 bg-gray-100 text-gray-500 text-sm border-r border-gray-300 whitespace-nowrap">
                    /news/
                  </span>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={(e) => {
                      const v = e.target.value.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 80);
                      setFormData(prev => ({ ...prev, slug: v }));
                    }}
                    placeholder={(formData.title || '').toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'auto-generated-from-title'}
                    className="flex-1 px-4 py-3 focus:outline-none"
                    data-testid="news-slug-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Custom URL for SEO. Lowercase letters, numbers, and dashes only. Leave blank to auto-generate.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                <input
                  type="number"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  min="2000"
                  max="2100"
                  placeholder="e.g., 2025"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter the year (e.g., 2025)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Short Content (Summary)</label>
                <textarea
                  name="short_content"
                  value={formData.short_content}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#20B2AA]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content * (Rich Text)</label>
                <div className="border border-gray-300 rounded-xl quill-container">
                  <ReactQuill
                    value={formData.content}
                    onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    placeholder="Write your content here... Use the toolbar for formatting"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Use H1, H2, H3 for headings. Bold, Italic, Strike for text formatting. Add links using the link icon.</p>
                <style>{`
                  .quill-container {
                    position: relative;
                  }
                  .quill-container .ql-picker-options {
                    z-index: 9999 !important;
                    position: absolute !important;
                  }
                  .quill-container .ql-header .ql-picker-options {
                    background: white;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                  }
                `}</style>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Images (Multiple)
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  📸 Recommended size: <strong>1200 x 800 pixels</strong> for best quality
                </p>
                
                {/* Image Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-[#20B2AA] transition-colors mb-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="news-image-upload"
                    disabled={uploading}
                  />
                  <label htmlFor="news-image-upload" className="cursor-pointer">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">
                      {uploading ? 'Uploading...' : 'Click to upload images (multiple allowed)'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB each</p>
                  </label>
                </div>

                {/* Images Grid Preview */}
                {formData.images && formData.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={getImageUrl(img)} 
                          alt={`Preview ${index + 1}`} 
                          className="w-full h-32 object-cover rounded-xl" 
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Legacy Image URL Input (hidden but kept for compatibility) */}
                <input
                  type="hidden"
                  name="image_url"
                  value={formData.image_url}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-black rounded-xl py-6"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#20B2AA] hover:bg-[#189a93] text-white rounded-xl py-6"
                >
                  {editingArticle ? 'Update Article' : 'Create Article'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;